// İcra takip hesap motoru.
// UYAP benzeri kalemleri (asıl alacak, işlemiş/işleyen faiz, vekalet ücreti,
// masraf, tahsil harcı, toplam alacak) hesaplar. Faiz için utils/faiz.js kullanılır.

import { hesaplaFaiz, bugunISO } from './faiz';

// ===========================================================================
// AAÜT NİSPİ VEKALET ÜCRETİ TARİFESİ — 2026 (Üçüncü Kısım)
// ---------------------------------------------------------------------------
// Kaynak: Avukatlık Asgari Ücret Tarifesi, 04.11.2025 tarihli ve 33067 sayılı
// Resmî Gazete (2025-2026 dönemi). Üçüncü Kısım nispi ücret dilimleri.
// Her dilim: o dilime düşen tutara uygulanacak yüzde (kademeli/progressive).
// Dilimlerin toplamı: 18.600.000 TL; üzeri için %1.
// ===========================================================================
export const ICRA_VEKALET_TARIFESI = [
  { limit: 600000, oran: 16 },      // İlk 600.000 TL
  { limit: 600000, oran: 15 },      // Sonraki 600.000 TL
  { limit: 1200000, oran: 14 },     // Sonraki 1.200.000 TL
  { limit: 1200000, oran: 13 },     // Sonraki 1.200.000 TL (2026'da eklenen dilim)
  { limit: 1800000, oran: 11 },     // Sonraki 1.800.000 TL
  { limit: 2400000, oran: 8 },      // Sonraki 2.400.000 TL
  { limit: 3000000, oran: 5 },      // Sonraki 3.000.000 TL
  { limit: 3600000, oran: 3 },      // Sonraki 3.600.000 TL
  { limit: 4200000, oran: 2 },      // Sonraki 4.200.000 TL
  { limit: Infinity, oran: 1 }      // 18.600.000 TL üzeri
];

// AAÜT 2026 — İcra dairelerinde yapılan (genel) takipler için maktu vekalet ücreti.
// (Tahliye 20.000, icra mahkemesi işleri 11.000/18.000 gibi ayrı maktular vardır.)
export const MAKTU_ICRA_VEKALET_2026 = 9000;

// AAÜT 2026 m.11: takip miktarı bu tutara kadar olan icra takiplerinde
// vekalet ücreti MAKTU uygulanır; üzerinde nispi (Üçüncü Kısım) hesaplanır.
export const ICRA_NISPI_ESIK_2026 = 56250;

// Kademeli (progressive) nispi vekalet ücreti (Üçüncü Kısım)
/**
 * Turkce yazimli tutari sayiya cevirir: "12.000" -> 12000,
 * "12.000,50" -> 12000.5. Elle girilen vekalet ucreti metin alanindan
 * geldigi icin gerekli.
 */
export const parseTurkceTutar = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/s/g, '');
  if (!s) return 0;
  if (s.includes(',')) s = s.replace(/./g, '').replace(',', '.');
  else if (/^d{1,3}(.d{3})+$/.test(s)) s = s.replace(/./g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export const icraVekaletUcreti = (matrah) => {
  let kalan = Number(matrah) || 0;
  if (kalan <= 0) return 0;
  let ucret = 0;
  for (const d of ICRA_VEKALET_TARIFESI) {
    if (kalan <= 0) break;
    const dilimTutar = Math.min(kalan, d.limit);
    ucret += dilimTutar * (d.oran / 100);
    kalan -= dilimTutar;
  }
  return ucret;
};

// ===========================================================================
// TAHSİL HARCI (492 sayılı Harçlar Kanunu — (1) sayılı tarife, İcra harçları)
// ---------------------------------------------------------------------------
// Aşamalar: hacizden önce / hacizden sonra-satıştan önce / satıştan sonra.
// Oranlar periyodik güncellenir; gerekirse buradan düzeltin.
// ===========================================================================
export const TAHSIL_HARCI_ORANLARI = {
  once: 4.55,   // Hacizden önce ödenen
  sonra: 9.10,  // Hacizden sonra, satıştan önce ödenen
  satis: 11.38  // Satıştan sonra ödenen
};

export const TAHSIL_ASAMA_LABELS = {
  once: 'Hacizden önce (%4,55)',
  sonra: 'Hacizden sonra (%9,10)',
  satis: 'Satış sonrası (%11,38)'
};

export const tahsilHarci = (matrah, asama) => {
  const oran = TAHSIL_HARCI_ORANLARI[asama] ?? TAHSIL_HARCI_ORANLARI.once;
  return (Number(matrah) || 0) * (oran / 100);
};

// Cezaevi (yapı) harcı — 2548 sayılı Kanun: tahsil edilen paranın %2'si.
// Tahsilatta alacaklıdan kesilir; bilgi amaçlı gösterilir.
export const CEZAEVI_HARCI_ORANI = 2;
export const cezaeviHarci = (matrah) => (Number(matrah) || 0) * (CEZAEVI_HARCI_ORANI / 100);

// ===========================================================================
// İCRA AÇILIŞ MASRAFLARI — 2026
// ---------------------------------------------------------------------------
// Kaynak: Harçlar Kanunu Genel Tebliği (Seri No: 98) — 31.12.2025 tarihli
// 33124 mükerrer Resmî Gazete, 01.01.2026'dan geçerli + PTT 2026 tarifesi.
// Maktu tutarlar her yıl yeniden değerleme ile güncellenir; gerekirse buradan
// güncelleyin. Tebligat borçlu başına hesaplanır.
// ===========================================================================
export const ICRA_MASRAF_2026 = {
  basvurmaHarci: 732,        // İcraya başvurma harcı (maktu) — Harçlar K. Tebliği Seri No: 98
  vekaletSuretHarci: 104,    // Vekaletname suret harcı
  baroPulu: 164,             // Vekalet (baro) pulu — UYAP Dosya Hesabı masrafına genelde dahil değil
  dosyaGiderAvansi: 30,      // Fiziki dosya / gider avansı (çoğu UYAP dosyasında yok)
  tebligatBorcluBasi: 265,   // Posta / masraf avansı (UYAP 2026 tahsilat — borçlu başına)
  etebligatSirket: 20,       // Şirket müvekkil e-tebligat
  pesinHarcOrani: 0.5        // İlamsız takipte peşin harç: binde 5 (%0,5) — tahsil harcına mahsup
};

export const ICRA_ACILIS_MASRAF_TURU = 'İCRA AÇILIŞ MASRAFLARI';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// İcra açılış masraflarını kalem kalem hesapla.
// params: { takipMiktari, pesinMatrah, borcluSayisi=1, ilamsiz=true, isSirket=false }
// pesinMatrah: UYAP uyumu — asıl + takip tarihine kadar işlemiş faiz; yoksa takipMiktari
export const icraAcilisMasraflari = (params) => {
  const {
    takipMiktari = 0,
    pesinMatrah = null,
    borcluSayisi = 1,
    ilamsiz = true,
    isSirket = false
  } = params || {};
  const m = ICRA_MASRAF_2026;
  const adet = Math.max(1, Number(borcluSayisi) || 1);
  const matrah = Number(pesinMatrah != null ? pesinMatrah : takipMiktari) || 0;
  const pesinHarc = ilamsiz ? round2(matrah * (m.pesinHarcOrani / 100)) : 0;
  const tebligat = m.tebligatBorcluBasi * adet;

  const kalemler = [
    { key: 'basvurma', ad: 'İcraya başvurma harcı', tutar: m.basvurmaHarci },
    { key: 'pesin', ad: `Peşin harç (binde 5 — tahsil harcına mahsup${ilamsiz ? '' : ' · ilamlıda yok'})`, tutar: pesinHarc },
    { key: 'suret', ad: 'Vekaletname suret harcı', tutar: m.vekaletSuretHarci },
    { key: 'baro', ad: 'Vekalet (baro) pulu', tutar: m.baroPulu },
    { key: 'tebligat', ad: `Posta / masraf avansı (${adet} borçlu)`, tutar: tebligat }
  ];
  if (isSirket) {
    kalemler.push({ key: 'etebligat', ad: 'E-tebligat (şirket)', tutar: m.etebligatSirket });
  }
  kalemler.push({ key: 'dosya', ad: 'Dosya gider avansı', tutar: m.dosyaGiderAvansi });
  const toplam = kalemler.reduce((s, k) => s + (Number(k.tutar) || 0), 0);
  return { kalemler, toplam };
};

// UYAP "takip masrafı toplam" (açılış harç + avans), ör. 1.608,06:
// başvurma 732 + peşin (kesinleşen×‰5) + suret 104 + baro 164 + masraf avansı 265.
export const DEFAULT_MASRAF_KALEM_DAHIL = {
  basvurma: true,
  pesin: true,
  suret: true,
  baro: true,
  tebligat: true,
  etebligat: false,
  dosya: false
};

/** Masraf kalem varsayılan sürümü — eski kayıtlarda UYAP uyumuna zorla geçiş için */
export const MASRAF_KALEM_SURUM = 7;

/** UYAP Dosya Hesabı → Masraf Miktarı = başvurma + vekalet suret (ör. 732+104=836) */
export const UYAP_DOSYA_HESABI_MASRAF_KEYS = ['basvurma', 'suret'];

export const uyapDosyaHesabiMasraf = (kalemler) => {
  const keys = new Set(UYAP_DOSYA_HESABI_MASRAF_KEYS);
  return round2((kalemler || []).reduce((s, k) => (
    keys.has(k.key) ? s + (Number(k.tutar) || 0) : s
  ), 0));
};

/** Seçili kalemlere göre masraf toplamı (UYAP'taki masraf ile eşleştirmek için kalem tiklenebilir) */
export const masrafToplamFromKalemler = (kalemler, dahilMap) => {
  const d = { ...DEFAULT_MASRAF_KALEM_DAHIL, ...(dahilMap || {}) };
  return (kalemler || []).reduce((s, k) => {
    if (k.key && d[k.key] === false) return s;
    return s + (Number(k.tutar) || 0);
  }, 0);
};

// ===========================================================================
// TOPLU İCRA HESABI (canlı — bugüne göre)
// ---------------------------------------------------------------------------
// params:
//   asilAlacak       : takip açılış (asıl) miktarı
//   faizBaslangic    : temerrüt/faiz başlangıç tarihi (YYYY-MM-DD)
//   takipTarihi      : takip tarihi (YYYY-MM-DD) — faizi ikiye böler
//   faizTuru         : 'Yasal' | 'Avans' | 'Sozlesme'
//   faizOrani        : avans (manuel) / sözleşme için yıllık oran
//   oranManuel       : Avans'ta true ise tablo yerine faizOrani (sabit) kullanılır
//   masrafToplam     : takip masrafları toplamı
//   tahsilAsama      : 'once' | 'sonra' | 'satis'
//   vekaletManuel    : elle girilen vekalet ücreti (varsa tarife yerine kullanılır)
//   bugun            : (opsiyonel) hesap günü; verilmezse bugün
// ===========================================================================
export const hesaplaIcra = (params) => {
  const {
    asilAlacak = 0,
    faizBaslangic = '',
    takipTarihi = '',
    faizTuru = 'Yasal',
    faizOrani = 0,
    oranManuel = false,
    masrafToplam = 0,
    tahsilAsama = 'once',
    vekaletManuel = null,
    ucdeCeyrek = false, // ödeme emri süresinde ödeme → tarifenin ¾'ü
    kesinlesenUyap = null, // UYAP "Takipte Kesinleşen Miktar" — vekalet/tahsil/peşin matrahı
    faizTakibeDahil = false, // takip açılış tutarı zaten asıl+işlemiş (UYAP tek kalem)
    bugun = bugunISO()
  } = params || {};

  const asil = Number(asilAlacak) || 0;

  // Takip açılış (asıl) miktarı girilmemişse hiçbir hesaplama yapma — her şey sıfır.
  if (asil <= 0) {
    return {
      asil: 0,
      kesinlesen: 0,
      gecmisFaiz: 0,
      isleyenFaiz: 0,
      toplamFaiz: 0,
      gecmisFaizDetay: { faiz: 0, segmentler: [], toplamGun: 0 },
      isleyenFaizDetay: { faiz: 0, segmentler: [], toplamGun: 0 },
      vekaletMatrah: 0,
      vekaletTarife: 0,
      vekalet: 0,
      vekaletTavanUygulandi: false,
      vekaletManuelKullanildi: false,
      masraf: 0,
      uyapMasraf: 0,
      toplamAlacak: 0,
      uyapToplamAlacak: 0,
      tahsilAsama,
      tahsilHarci: 0,
      cezaeviHarci: 0,
      kesinlesenUyapKullanildi: false,
      faizTakibeDahil: false,
      bugun
    };
  }

  // Faiz aralıkları: geçmiş (başlangıç→takip) ve işleyen (takip→bugün)
  // Takip tarihi yoksa tüm faiz "işleyen" kabul edilir (başlangıç→bugün).
  let gecmisFaizDetay = { faiz: 0, segmentler: [], toplamGun: 0 };
  let isleyenFaizDetay = { faiz: 0, segmentler: [], toplamGun: 0 };

  if (faizBaslangic) {
    if (takipTarihi) {
      gecmisFaizDetay = hesaplaFaiz(asil, faizBaslangic, takipTarihi, faizTuru, faizOrani, oranManuel);
      isleyenFaizDetay = hesaplaFaiz(asil, takipTarihi, bugun, faizTuru, faizOrani, oranManuel);
    } else {
      isleyenFaizDetay = hesaplaFaiz(asil, faizBaslangic, bugun, faizTuru, faizOrani, oranManuel);
    }
  }

  const gecmisFaiz = gecmisFaizDetay.faiz;
  const isleyenFaiz = isleyenFaizDetay.faiz;
  const toplamFaiz = gecmisFaiz + isleyenFaiz;

  // Takip talebi: asıl + geçmiş gün faizi ayrı yazılır.
  // UYAP Dosya Hesabı "Takipte Kesinleşen" = asıl + geçmiş (işlemiş) faiz.
  // İşleyen faiz yalnız ASIL üzerinden (geçmiş faize faiz yok).
  const uyapKesin = Number(kesinlesenUyap);
  const kesinlesen = (Number.isFinite(uyapKesin) && uyapKesin > 0)
    ? uyapKesin
    : (asil + gecmisFaiz);

  // ---------------------------------------------------------------------
  // VEKALET ÜCRETİ — AAÜT m.11/1
  //
  // Tarifenin sözü (04.11.2025 · 33067 s. RG):
  //   "...avukatlık ücreti, bu Tarifenin üçüncü kısmına göre belirlenir.
  //    Şu kadar ki takip miktarı 56.250,00 TL'ye kadar olan icra
  //    takiplerinde avukatlık ücreti, bu Tarifenin ikinci kısmının
  //    ikinci bölümünde, icra dairelerindeki takipler için öngörülen
  //    maktu ücrettir. ANCAK, BU ÜCRET ASIL ALACAĞI GEÇEMEZ."
  //
  // Son cümle daha önce uygulanmıyordu: küçük takiplerde sistem asıl
  // alacaktan büyük ücret hesaplıyordu (5.000 TL alacak için 9.000 TL
  // gibi). Artık asıl alacak tavan olarak uygulanıyor.
  // ---------------------------------------------------------------------
  const vekaletMatrah = kesinlesen;
  let vekaletTarife;
  let vekaletTavanUygulandi = false;

  if (kesinlesen <= ICRA_NISPI_ESIK_2026) {
    vekaletTarife = MAKTU_ICRA_VEKALET_2026;
    // m.11/1 son cümle — tavan ASIL ALACAK, kesinleşen değil.
    if (asil > 0 && vekaletTarife > asil) {
      vekaletTarife = asil;
      vekaletTavanUygulandi = true;
    }
  } else {
    vekaletTarife = icraVekaletUcreti(vekaletMatrah);
  }

  // m.11/4: borçlu ödeme süresi içinde öderse ücretin dörtte üçü.
  // Tavan uygulandıktan SONRA hesaplanır.
  if (ucdeCeyrek) vekaletTarife = vekaletTarife * 0.75;

  const vekalet =
    vekaletManuel !== null && vekaletManuel !== undefined && vekaletManuel !== ''
      ? (typeof vekaletManuel === 'string' ? parseTurkceTutar(vekaletManuel) : (Number(vekaletManuel) || 0))
      : vekaletTarife;

  const masraf = Number(masrafToplam) || 0;

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const gecmisR = round2(gecmisFaiz);
  const isleyenR = round2(isleyenFaiz);
  const vekaletR = round2(vekalet);
  const masrafR = round2(masraf);

  // Tahsil / peşin / vekalet — UYAP kesinleşen üzerinden
  const kesinlesenR = round2(kesinlesen);
  const tahsilHarciTutar = round2(tahsilHarci(kesinlesenR, tahsilAsama));
  const cezaeviHarciTutar = round2(cezaeviHarci(kesinlesenR));
  const uyapMasraf = round2(ICRA_MASRAF_2026.basvurmaHarci + ICRA_MASRAF_2026.vekaletSuretHarci);
  // UYAP Dosya Hesabı toplamı: kesinleşen + işleyen faiz + vekalet + masraf(836) + tahsil
  const uyapToplamAlacak = round2(kesinlesenR + isleyenR + vekaletR + uyapMasraf + tahsilHarciTutar);

  const toplamAlacak = round2(asil + gecmisR + isleyenR + vekaletR + masrafR + tahsilHarciTutar);

  return {
    asil: round2(asil),
    kesinlesen: kesinlesenR,
    gecmisFaiz: gecmisR,
    isleyenFaiz: isleyenR,
    toplamFaiz: round2(gecmisR + isleyenR),
    gecmisFaizDetay,
    isleyenFaizDetay,
    vekaletMatrah: round2(vekaletMatrah),
    vekaletTarife: round2(vekaletTarife),
    vekalet: vekaletR,
    // AAUT m.11/1: ucret asil alacagi gecemez — tavan uygulandi mi?
    vekaletTavanUygulandi,
    vekaletManuelKullanildi: !(vekaletManuel === null || vekaletManuel === undefined || vekaletManuel === ''),
    masraf: masrafR,
    uyapMasraf,
    toplamAlacak,
    uyapToplamAlacak,
    tahsilAsama,
    tahsilHarci: tahsilHarciTutar,
    cezaeviHarci: cezaeviHarciTutar,
    kesinlesenUyapKullanildi: Number.isFinite(uyapKesin) && uyapKesin > 0,
    faizTakibeDahil: !!faizTakibeDahil,
    bugun
  };
};
